/**
 * Stock de publicaciones en una lista catastral (mismo catálogo que CAD).
 * Las UF siempre tienen estado (vacío → disponible). El lote solo catastro no.
 */

import { STOCK_STATUSES } from './listingCoreConstants';
import { RECORD_KIND } from './recordCards';
import { mapStockStatusFromApi } from './userLotFormUtils';
import { getDisplayUnits } from './userLotUnits';

/**
 * Estados elegidos. Sin ids válidos → `null` (todos).
 * @param {string|string[]|null|undefined} statusFilter
 * @returns {string[]|null}
 */
export function normalizeStockStatusFilter(statusFilter) {
  const list = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
  const ids = [...new Set(list.filter((id) => STOCK_STATUSES.includes(id)))];
  return ids.length ? ids : null;
}

const publicationStatus = (source) => {
  if (!source || typeof source !== 'object') return null;
  const raw = source.stock_status;
  if (raw == null || String(raw).trim() === '') return 'available';
  return mapStockStatusFromApi(source);
};

const lotContentSource = (lot) => {
  if (lot?.lot_content && typeof lot.lot_content === 'object') return lot.lot_content;
  if (lot?.lot_user_content && typeof lot.lot_user_content === 'object') return lot.lot_user_content;
  return null;
};

const lotHasPublicationContent = (lot) => {
  if (lot?.has_lot_content === true) return true;
  const content = lotContentSource(lot);
  if (!content) return false;
  return Boolean(
    content.stock_status
    || String(content.title || '').trim()
    || content.price != null,
  );
};

/** Estados de las publicaciones de un lote agrupado (UF + contenido de lote). */
export function getLotRowStockStatuses(lot) {
  const statuses = getDisplayUnits(lot).map((unit) => publicationStatus(unit));
  const content = lotContentSource(lot);
  if (lotHasPublicationContent(lot) && content && Object.prototype.hasOwnProperty.call(content, 'stock_status')) {
    statuses.push(publicationStatus(content));
  }
  return statuses.filter(Boolean);
}

export function lotRowMatchesStockStatus(lot, statusFilter) {
  const selected = normalizeStockStatusFilter(statusFilter);
  if (!selected) return true;
  const statuses = getLotRowStockStatuses(lot);
  if (!statuses.length) return false;
  return statuses.some((status) => selected.includes(status));
}

/** Estado de una card de registro. UF vacía → disponible; lote sin publicación → null. */
export function getRecordStockStatus(record) {
  if (!record) return null;
  if (record.record_kind === RECORD_KIND.UNIT) {
    return record.stock_status && STOCK_STATUSES.includes(record.stock_status)
      ? record.stock_status
      : 'available';
  }
  if (record.stock_status && STOCK_STATUSES.includes(record.stock_status)) {
    return record.stock_status;
  }
  return null;
}

export function recordMatchesStockStatus(record, statusFilter) {
  const selected = normalizeStockStatusFilter(statusFilter);
  if (!selected) return true;
  const status = getRecordStockStatus(record);
  return Boolean(status && selected.includes(status));
}

/**
 * Conteos por estado sobre las publicaciones de la lista (no sobre el lote catastral).
 * `total` es lo que muestra «Todos · N».
 * @returns {{ counts: Record<string, number>, total: number }}
 */
export function countListStockStatuses(lots) {
  const counts = Object.fromEntries(STOCK_STATUSES.map((status) => [status, 0]));
  let total = 0;
  (Array.isArray(lots) ? lots : []).forEach((lot) => {
    const statuses = getLotRowStockStatuses(lot);
    if (statuses.length) {
      statuses.forEach((status) => {
        if (counts[status] != null) counts[status] += 1;
        total += 1;
      });
      return;
    }
    total += 1;
  });
  return { counts, total };
}
